import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Alert, DatePicker, Form, FormInstance, Input, InputNumber, Space, TableColumnsType } from 'antd';
import dayjs from 'dayjs';

import {
  Crud,
  CrudMode,
  IconButton,
  renderCrudFormSortableGroup,
  notification,
  rejectionError,
  transformCrudFormErrors,
} from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudFormGetDraggableRow, CrudProps, FormValues, RenderFormItem, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfErcProfileCrudRequest,
  ErcProfilesCreateChangeRequestResponse,
  ErcValidationErrors,
  UsageResponse,
  ercProfilesCheckUsage,
  ercProfilesCreateChangeRequest,
} from '@pmp/api';
import {
  crudToActionMap,
  getApiErrorMessage,
  changeRequestNotificationMessage,
  changeRequestButtonMessage,
  DATE_FORMAT,
  formatDecimals,
  getDecimalsByField,
} from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { FormGeneralErrors, ReadonlyBandsTable } from '@pmp/components';

import { getFormSchema } from '../formConfig';
import { periodColumns } from '../tableConfig';
import type { ErcProfileCrudFromData, ErcProfilesCrudFormProps, PenaltyTierFormData } from '../interfaces';
import styles from '../styles.module.css';

const TIER_DECIMALS = getDecimalsByField(periodColumns);

export const ErcProfilesCrudForm = memo(({ mode, record, refreshTable, onClose }: ErcProfilesCrudFormProps): ReactElement | null => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(false);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const [penaltyTiersErrors, setPenaltyTiersErrors] = useState<string[]>([]);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }
    if (!record?.ercProfileId) {
      notification().error({ title: t('global.errors.unexpected') });
      handleOnClose();
      return;
    }
    ercProfilesCheckUsage({ path: { ercProfileId: record.ercProfileId } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
        handleOnClose();
      });
  }, [mode, record, isUsed, t, handleOnClose]);

  const onConfirm: (formData: ErcProfileCrudFromData) => Promise<ValidationError[] | void> = useCallback(
    ({ rollingOrFixed, ...values }) => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));
      setPenaltyTiersErrors([]);
      setGeneralErrors([]);
      const penaltyTiers = (values.penaltyTiers ?? [{}]).map((band, index) => ({
        order: index + 1,
        endDate: rollingOrFixed && band.endDate ? dayjs(band.endDate).format(DATE_FORMAT) : undefined,
        durationInMonths: rollingOrFixed ? undefined : band.durationInMonths,
        penaltyPercentage: band.penaltyPercentage,
      }));
      const body: ChangeRequestOfErcProfileCrudRequest = {
        data: { ...values, penaltyTiers },
        actionType: crudToActionMap[mode],
      };
      return ercProfilesCreateChangeRequest({ body }).then(
        ({ data: { isValid, errors } }: { data: ErcProfilesCreateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }
            const { penaltyTiersErrors: tiersErrors, generalErrors: formErrors, ...fieldErrors }: ErcValidationErrors = errors ?? {};
            setPenaltyTiersErrors(tiersErrors ?? []);
            setGeneralErrors(formErrors ?? []);
            const penaltyTiers = Object.fromEntries(
              Object.entries(fieldErrors.penaltyTiers ?? {}).map(([index, penaltyTierErrors]) => [
                index,
                {
                  ...penaltyTierErrors,
                  durationInMonths: rollingOrFixed ? [] : penaltyTierErrors.durationInMonths,
                  endDate: rollingOrFixed ? penaltyTierErrors.endDate : [],
                },
              ]),
            );
            const allErrors = transformCrudFormErrors({ ...fieldErrors, penaltyTiers });
            return allErrors;
          }
          if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
          notification().success({
            title: changeRequestNotificationMessage(t, enableFourEyes),
          });
        },
      );
    },
    [mode, refreshTable, t, enableFourEyes],
  );

  const getRow: CrudFormGetDraggableRow = useCallback(
    (index, onDeleteRow) => {
      const disabled = mode === CrudMode.VIEW || !!isUsed;
      const fixed = ref.current?.form.getFieldValue(['rollingOrFixed']);

      return (
        <div className={styles.ercBandRow}>
          {fixed ? (
            <div className={styles.ercBandCell}>
              <Form.Item className={styles.ercBandFormItem} name={['penaltyTiers', index as number, 'endDate']} dependencies={['type']}>
                <DatePicker disabled={disabled} placeholder="" />
              </Form.Item>
            </div>
          ) : (
            <div className={styles.ercBandCell}>
              <Form.Item
                className={styles.ercBandFormItem}
                name={['penaltyTiers', index as number, 'durationInMonths']}
                dependencies={['type']}
              >
                <InputNumber disabled={disabled} precision={0} placeholder="" />
              </Form.Item>
            </div>
          )}
          <div className={styles.ercBandCell}>
            <Form.Item className={styles.ercBandFormItem}>
              <Space.Compact>
                <Form.Item name={['penaltyTiers', index as number, 'penaltyPercentage']} noStyle>
                  <InputNumber disabled={disabled} precision={TIER_DECIMALS.penaltyPercentage} placeholder="" />
                </Form.Item>
                <Input className={styles.currencyPrefix} defaultValue="%" disabled />
              </Space.Compact>
            </Form.Item>
          </div>
          <div className={styles.ercBandDeleteCell}>
            <IconButton icon={<DeleteOutlined />} onClick={() => onDeleteRow?.(index)} disabled={disabled || !onDeleteRow} />
          </div>
        </div>
      );
    },
    [isUsed, mode],
  );

  const showErcInUseAlert = mode === CrudMode.EDIT && !!isUsed;

  const renderAlert: RenderFormItem<{ index: number }> = useCallback(() => {
    if (!generalErrors.length && !penaltyTiersErrors.length && !showErcInUseAlert) return null;

    return (
      <>
        <FormGeneralErrors errors={[...generalErrors, ...penaltyTiersErrors]} />
        {showErcInUseAlert && <Alert message={t('pmp.erc.labels.ercInUseMessage')} type="warning" showIcon />}
      </>
    );
  }, [showErcInUseAlert, t, generalErrors, penaltyTiersErrors]);

  const renderErcBands: RenderFormItem<{ index: number }> = useCallback(
    (schema, config, form) => {
      const addButton = { label: t('pmp.fees.labels.addBand') };
      const readonly = mode === CrudMode.VIEW;
      const disabled = readonly || !!isUsed;
      const minLength = 1;
      const values = form as FormInstance | undefined;
      const fixed = values?.getFieldValue(['rollingOrFixed']);
      const tiers = (values?.getFieldValue(['penaltyTiers']) as PenaltyTierFormData[] | undefined) ?? [];

      if (readonly) {
        const empty = '-';
        const columns: TableColumnsType<PenaltyTierFormData> = [
          fixed
            ? {
                title: t('pmp.erc.labels.endDate'),
                dataIndex: 'endDate',
                render: (endDate?: PenaltyTierFormData['endDate']) => endDate?.format(DATE_FORMAT) ?? empty,
              }
            : {
                title: t('pmp.erc.labels.durationInMonths'),
                dataIndex: 'durationInMonths',
                render: (months?: number | null) => months ?? empty,
              },
          {
            title: t('pmp.erc.labels.ercPercentage'),
            dataIndex: 'penaltyPercentage',
            render: (percentage?: number | null) =>
              percentage == null ? empty : `${formatDecimals(percentage, TIER_DECIMALS.penaltyPercentage)}%`,
          },
        ];

        return <ReadonlyBandsTable<PenaltyTierFormData> columns={columns} dataSource={tiers} />;
      }

      const getNewBand = (bands: Array<PenaltyTierFormData>): PenaltyTierFormData => {
        const previous = bands[bands.length - 1];
        if (fixed) return previous?.endDate ? { endDate: dayjs(previous.endDate).add(1, 'year') } : {};
        return typeof previous?.durationInMonths === 'number' ? { durationInMonths: previous.durationInMonths } : {};
      };

      return (
        <>
          <div className={styles.ercBandsTitles}>
            <div className={`${styles.ercBandCell} ${styles.bandHeading}`}>
              {fixed ? t('pmp.erc.labels.endDate') : t('pmp.erc.labels.durationInMonths')}
            </div>
            <div className={`${styles.ercBandCell} ${styles.bandHeading}`}>{t('pmp.erc.labels.ercPercentage')}</div>
            <div className={styles.ercBandDeleteCell} />
          </div>
          {renderCrudFormSortableGroup({
            schema,
            config,
            form,
            addButton,
            getRow,
            disabled,
            minLength,
            setDisableSave: ref.current?.setDisableSave,
            getNewRow: getNewBand,
          })}
        </>
      );
    },
    [t, mode, isUsed, getRow],
  );

  const onValuesChange = useCallback((changed: FormValues) => {
    if (changed.rollingOrFixed !== true) return;
    const form = ref.current?.form;
    if (!form) return;
    const bands: Array<PenaltyTierFormData> = form.getFieldValue(['penaltyTiers']) ?? [{}];
    let previousEndDate = dayjs();
    const bandsWithEndDates = bands.map((band, index) => {
      if (band?.endDate) {
        previousEndDate = dayjs(band.endDate);
        return band;
      }
      if (typeof band?.durationInMonths !== 'number') return index === 0 ? { ...band, endDate: previousEndDate } : band;
      previousEndDate = previousEndDate.add(band.durationInMonths, 'month');
      return { ...band, endDate: previousEndDate };
    });
    form.setFieldValue(['penaltyTiers'], bandsWithEndDates);
  }, []);

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.CREATE]: t('enso.labels.createX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.EDIT]: t('enso.labels.editX', { x: t('pmp.erc.labels.ercProfile') }),
    };
    return titles[mode];
  }, [mode, t]);

  const crudProps: CrudProps<ErcProfileCrudFromData> = useMemo(() => {
    const rollingOrFixed = !!record?.penaltyTiers?.[0]?.endDate;

    return {
      ref,
      open,
      schema: getFormSchema(t, renderErcBands, renderAlert, mode, isUsed),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('enso.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues: {
        ...record,
        penaltyTiers: record?.penaltyTiers?.map(tier => ({
          ...tier,
          endDate: tier.endDate ? dayjs(tier.endDate) : undefined,
        })),
        rollingOrFixed,
      },
      mode,
      readonly: mode === CrudMode.VIEW,
      width: 'm',
      onClose: handleOnClose,
      onConfirm,
      onValuesChange,
    };
  }, [record, open, renderErcBands, renderAlert, mode, isUsed, title, t, handleOnClose, onConfirm, onValuesChange, enableFourEyes]);

  return <Crud {...crudProps} />;
});
